import React, { useState, memo } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';

// TopoJSON world map
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Countries with active users - using ISO 3166-1 numeric codes
const activeCountries = {
  '840': { name: 'United States', users: '12,500+' },
  '124': { name: 'Canada', users: '3,200+' },
  '826': { name: 'United Kingdom', users: '8,400+' },
  '276': { name: 'Germany', users: '5,100+' },
  '250': { name: 'France', users: '4,300+' },
  '724': { name: 'Spain', users: '2,100+' },
  '380': { name: 'Italy', users: '1,800+' },
  '528': { name: 'Netherlands', users: '1,500+' },
  '752': { name: 'Sweden', users: '1,200+' },
  '578': { name: 'Norway', users: '800+' },
  '36': { name: 'Australia', users: '4,600+' },
  '554': { name: 'New Zealand', users: '900+' },
  '392': { name: 'Japan', users: '6,200+' },
  '410': { name: 'South Korea', users: '3,800+' },
  '702': { name: 'Singapore', users: '2,400+' },
  '356': { name: 'India', users: '9,500+' },
  '784': { name: 'UAE', users: '1,600+' },
  '76': { name: 'Brazil', users: '3,400+' },
  '484': { name: 'Mexico', users: '2,200+' },
  '710': { name: 'South Africa', users: '1,100+' },
  '376': { name: 'Israel', users: '1,900+' },
  '616': { name: 'Poland', users: '1,400+' },
  '756': { name: 'Switzerland', users: '1,700+' },
  '56': { name: 'Belgium', users: '1,000+' },
  '40': { name: 'Austria', users: '900+' },
  '372': { name: 'Ireland', users: '1,300+' },
  '620': { name: 'Portugal', users: '800+' },
  '208': { name: 'Denmark', users: '700+' },
  '246': { name: 'Finland', users: '600+' },
  '458': { name: 'Malaysia', users: '1,500+' },
  '764': { name: 'Thailand', users: '1,200+' },
  '608': { name: 'Philippines', users: '2,100+' },
  '360': { name: 'Indonesia', users: '2,800+' },
  '704': { name: 'Vietnam', users: '1,600+' },
  '152': { name: 'Chile', users: '900+' },
  '170': { name: 'Colombia', users: '1,100+' },
  '32': { name: 'Argentina', users: '1,400+' },
  '818': { name: 'Egypt', users: '800+' },
  '566': { name: 'Nigeria', users: '1,200+' },
  '404': { name: 'Kenya', users: '700+' },
  '682': { name: 'Saudi Arabia', users: '1,300+' },
  '792': { name: 'Turkey', users: '1,800+' },
  '643': { name: 'Russia', users: '2,500+' },
  '804': { name: 'Ukraine', users: '1,100+' },
  '203': { name: 'Czech Republic', users: '900+' },
  '642': { name: 'Romania', users: '700+' },
  '348': { name: 'Hungary', users: '600+' },
  '300': { name: 'Greece', users: '500+' },
  '158': { name: 'Taiwan', users: '2,000+' },
  '156': { name: 'China', users: '7,500+' },
};

const activeCountryIds = new Set(Object.keys(activeCountries));

const MapChart = memo(({ setTooltipContent }) => {
  return (
    <ComposableMap
      projection="geoMercator"
      projectionConfig={{
        scale: 140,
        center: [10, 30]
      }}
      style={{ width: '100%', height: 'auto' }}
    >
      <ZoomableGroup zoom={1} center={[0, 20]} minZoom={1} maxZoom={1}>
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const countryId = geo.id;
              const isActive = activeCountryIds.has(countryId);
              const countryInfo = activeCountries[countryId];
              
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() => {
                    if (isActive && countryInfo) {
                      setTooltipContent({
                        name: countryInfo.name,
                        users: countryInfo.users
                      });
                    }
                  }}
                  onMouseLeave={() => {
                    setTooltipContent(null);
                  }}
                  style={{
                    default: {
                      fill: isActive ? '#374151' : '#e5e7eb',
                      stroke: isActive ? '#4b5563' : '#d1d5db',
                      strokeWidth: 0.5,
                      outline: 'none',
                    },
                    hover: {
                      fill: isActive ? '#1f2937' : '#e5e7eb',
                      stroke: isActive ? '#111827' : '#d1d5db',
                      strokeWidth: isActive ? 1 : 0.5,
                      outline: 'none',
                      cursor: isActive ? 'pointer' : 'default',
                    },
                    pressed: {
                      fill: isActive ? '#111827' : '#e5e7eb',
                      outline: 'none',
                    },
                  }}
                  data-testid={isActive ? `country-${countryId}` : undefined}
                />
              );
            })
          }
        </Geographies>
      </ZoomableGroup>
    </ComposableMap>
  );
});

MapChart.displayName = 'MapChart';

export default function GlobalPresenceSection() {
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  return (
    <section 
      className="py-10 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-gray-50/50 to-white overflow-hidden" 
      data-testid="global-presence-section"
      onMouseMove={handleMouseMove}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-6 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-gray-900 mb-3 sm:mb-4">
            Used by teams worldwide
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-500 font-light max-w-2xl mx-auto">
            Teams and organizations across 50+ countries rely on Longfloat.
          </p>
        </div>

        {/* World Map Container */}
        <div className="relative w-full max-w-5xl mx-auto" data-testid="world-map">
          <div className="bg-[#fafafa] rounded-lg p-2 sm:p-4">
            <MapChart setTooltipContent={setTooltipContent} />
          </div>
          
          {/* Tooltip */}
          {tooltipContent && (
            <div
              className="fixed z-50 pointer-events-none"
              style={{
                left: tooltipPos.x + 12,
                top: tooltipPos.y - 60,
              }}
            >
              <div className="bg-gray-800 text-white px-4 py-3 rounded-lg shadow-lg">
                <div className="text-sm font-medium">{tooltipContent.name}</div>
                <div className="text-xs text-gray-400 mt-1">{tooltipContent.users} users</div>
              </div>
              <div 
                className="w-0 h-0 ml-4"
                style={{
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '6px solid #1f2937',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
